/**
 * AuthModal - Skool Over Training - Athlete Portal Authentication
 */

import { useState, useEffect, useRef } from 'react';
import {
  useAuth,
  getAvailableOAuthProviders,
  signInWithGoogle,
  signInWithApple,
  config,
  ValidatedInvite,
  updateProfile,
  getProfile,
  requestPasswordReset,
  confirmPasswordReset,
} from '@tss/shared';
import { Button, Input } from '@tss/shared/ui';
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';

interface AuthModalProps {
  onClose: () => void;
  validatedInvite?: ValidatedInvite | null;
  pendingTeamCode?: string | null; // For QR code signups
  /** Open the modal in a specific tab. Used by marketing-site CTAs that
   *  drop visitors directly into signup (e.g. the trial link from skool365.ai). */
  initialMode?: 'login' | 'register';
}

export function AuthModal({ validatedInvite, pendingTeamCode, initialMode }: AuthModalProps) {
  const { login, register, confirmCode, error, success, clearMessages } = useAuth();

  // Signup is allowed if globally enabled OR if there's a valid invite OR a pending team code
  const canSignup = config.signupEnabled || !!validatedInvite || !!pendingTeamCode;

  // Start in register mode if there's a valid invite, team code, or the
  // caller explicitly asked us to (e.g. the marketing-site trial CTA).
  const [mode, setMode] = useState<'login' | 'register' | 'confirm' | 'forgot' | 'reset'>(
    validatedInvite || pendingTeamCode || (initialMode === 'register' && canSignup)
      ? 'register'
      : 'login'
  );
  // Pre-fill email from invite (locked when invite present)
  const [email, setEmail] = useState(validatedInvite?.email || '');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmCodeValue, setConfirmCodeValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Forgot password state
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Update mode and email when validatedInvite changes (async validation)
  useEffect(() => {
    if (validatedInvite) {
      setMode('register');
      setEmail(validatedInvite.email);
    }
  }, [validatedInvite]);

  // Auto-focus first name field when invite is present
  const firstNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (validatedInvite && mode === 'register') {
      // Small delay to ensure the input is rendered
      setTimeout(() => firstNameRef.current?.focus(), 100);
    }
  }, [validatedInvite, mode]);

  const oauthProviders = getAvailableOAuthProviders();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    clearMessages();
    setResetSuccess('');

    try {
      if (mode === 'login') {
        await login(email, password);
      } else if (mode === 'register' && canSignup) {
        const result = await register(email, password, {
          givenName: firstName,
          familyName: lastName,
          inviteToken: validatedInvite?.token,
        });
        if (result.success) {
          if (result.autoConfirmed) {
            // User was auto-confirmed (invite-based signup), log them in
            const loggedIn = await login(email, password);
            if (loggedIn && (firstName || lastName)) {
              // IMPORTANT: Call getProfile() first to ensure the profile is created
              // with all Cognito claims (including email). This prevents a race condition
              // where updateProfile() could create an incomplete profile before
              // App.tsx's loadUserProfile() creates the full one.
              try {
                await getProfile();
              } catch (err) {
                console.error('Failed to initialize profile:', err);
              }
              // Now update the profile with the name they provided during signup
              try {
                await updateProfile({
                  given_name: firstName,
                  family_name: lastName,
                });
              } catch (err) {
                console.error('Failed to update profile with name:', err);
              }
            }
          } else {
            // Normal flow - needs email confirmation
            setMode('confirm');
          }
        }
      } else if (mode === 'confirm') {
        const result = await confirmCode(email, confirmCodeValue);
        if (result) {
          setMode('login');
        }
      } else if (mode === 'forgot') {
        // Request password reset code
        await requestPasswordReset(email);
        setResetSuccess('Verification code sent to your email');
        setMode('reset');
      } else if (mode === 'reset') {
        // Confirm password reset
        await confirmPasswordReset(email, resetCode, newPassword);
        setResetSuccess('Password reset successful! You can now sign in.');
        setResetCode('');
        setNewPassword('');
        setMode('login');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-modal">
      <div className="auth-card animate-scale-in">
        {/* Invite banner - prominent welcome when user has a valid invite */}
        {validatedInvite && mode === 'register' && (
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(var(--green-rgb), 0.2) 0%, rgba(var(--green-rgb), 0.15) 100%)',
              border: '1px solid rgba(var(--green-rgb), 0.5)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'rgba(var(--green-rgb), 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
              }}
            >
              <CheckCircle2 size={24} color="var(--color-success)" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: '#fff' }}>
              Welcome to {validatedInvite.org_name}!
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
              <strong style={{ color: 'var(--color-success)' }}>
                {validatedInvite.invited_by_name}
              </strong>{' '}
              invited you to join as an <strong>{validatedInvite.role.toLowerCase()}</strong>
            </p>
            <p
              style={{
                margin: '12px 0 0',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.5)',
                background: 'rgba(0,0,0,0.2)',
                padding: '8px 12px',
                borderRadius: '8px',
                display: 'inline-block',
              }}
            >
              Complete your account to get started
            </p>
          </div>
        )}

        {/* Team code banner - for QR code signups */}
        {pendingTeamCode && !validatedInvite && mode === 'register' && (
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(99, 102, 241, 0.15) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.5)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'rgba(59, 130, 246, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
              }}
            >
              <CheckCircle2 size={24} color="#3b82f6" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: '#fff' }}>
              Join Your Team!
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
              Create an account or sign in to join the team
            </p>
            <p
              style={{
                margin: '12px 0 0',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.5)',
                background: 'rgba(0,0,0,0.2)',
                padding: '8px 12px',
                borderRadius: '8px',
                display: 'inline-block',
              }}
            >
              Invite Code: <strong style={{ color: '#3b82f6' }}>{pendingTeamCode}</strong>
            </p>
          </div>
        )}

        {/* Logo - smaller when invite or team code present */}
        <div className="auth-header">
          {!validatedInvite && !pendingTeamCode && (
            <img
              src="/logo.png"
              alt="Skool Over Training"
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'contain',
                margin: '0 auto 20px',
              }}
            />
          )}
          <h1 className="auth-title">
            {mode === 'login' && 'Skool Over Training'}
            {mode === 'register' &&
              canSignup &&
              !validatedInvite &&
              !pendingTeamCode &&
              'Create Account'}
            {mode === 'register' && validatedInvite && 'Create Your Account'}
            {mode === 'register' && pendingTeamCode && !validatedInvite && 'Create Your Account'}
            {mode === 'confirm' && 'Verify Email'}
            {mode === 'forgot' && 'Reset Password'}
            {mode === 'reset' && 'New Password'}
          </h1>
          <p className="auth-subtitle">
            {mode === 'login' && 'Train anywhere, improve everywhere'}
            {mode === 'register' &&
              canSignup &&
              !validatedInvite &&
              !pendingTeamCode &&
              'Start your training journey today'}
            {mode === 'register' && validatedInvite && 'Just a few details to get you started'}
            {mode === 'register' &&
              pendingTeamCode &&
              !validatedInvite &&
              'Just a few details to get you started'}
            {mode === 'confirm' && 'Enter the code sent to your email'}
            {mode === 'forgot' && "Enter your email and we'll send you a code"}
            {mode === 'reset' && 'Enter the code and your new password'}
          </p>
        </div>

        {/* Error/Success messages */}
        {error && (
          <div
            style={{
              marginBottom: '20px',
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '10px',
              color: '#f87171',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}
        {(success || resetSuccess) && (
          <div
            style={{
              marginBottom: '20px',
              padding: '12px 16px',
              background: 'rgba(var(--green-rgb), 0.1)',
              border: '1px solid rgba(var(--green-rgb), 0.3)',
              borderRadius: '10px',
              color: 'var(--color-success)',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <CheckCircle2 size={18} />
            {success || resetSuccess}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'register' && canSignup && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>First Name</label>
                <div style={{ position: 'relative' }}>
                  <User
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'rgba(255,255,255,0.4)',
                    }}
                  />
                  <Input
                    ref={firstNameRef}
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                    style={{ paddingLeft: '44px' }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <Input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
          )}

          {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
            <div className="form-group">
              <label>
                Email{' '}
                {validatedInvite && (
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>
                    (from invite)
                  </span>
                )}
              </label>
              <div style={{ position: 'relative' }}>
                <Mail
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: validatedInvite ? 'var(--color-success)' : 'rgba(255,255,255,0.4)',
                  }}
                />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => !validatedInvite && setEmail(e.target.value)}
                  required
                  readOnly={!!validatedInvite}
                  placeholder="you@example.com"
                  style={{
                    paddingLeft: '44px',
                    ...(validatedInvite
                      ? {
                          background: 'rgba(var(--green-rgb), 0.1)',
                          borderColor: 'rgba(var(--green-rgb), 0.3)',
                          color: 'rgba(255,255,255,0.8)',
                          cursor: 'not-allowed',
                        }
                      : {}),
                  }}
                />
                {validatedInvite && (
                  <CheckCircle2
                    size={16}
                    style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--color-success)',
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {(mode === 'login' || mode === 'register') && (
            <div className="form-group">
              <label>Password</label>
              <div style={{ position: 'relative' }}>
                <Lock
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(255,255,255,0.4)',
                  }}
                />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{ paddingLeft: '44px', paddingRight: '44px' }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  iconLeft={showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '4px',
                    color: 'rgba(255,255,255,0.4)',
                  }}
                />
              </div>
              {mode === 'login' && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setMode('forgot');
                    clearMessages();
                    setResetSuccess('');
                  }}
                  style={{
                    fontSize: '13px',
                    padding: '4px 0',
                    marginTop: '8px',
                    textAlign: 'right',
                    width: '100%',
                    color: 'var(--purple-600)',
                  }}
                >
                  Forgot password?
                </Button>
              )}
            </div>
          )}

          {mode === 'reset' && (
            <>
              <div className="form-group">
                <label>Verification Code</label>
                <Input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  placeholder="Enter 6-digit code"
                  style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '0.3em' }}
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'rgba(255,255,255,0.4)',
                    }}
                  />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Min 8 chars, uppercase, lowercase, number"
                    style={{ paddingLeft: '44px', paddingRight: '44px' }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    iconLeft={showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      padding: '4px',
                      color: 'rgba(255,255,255,0.4)',
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {mode === 'confirm' && (
            <div className="form-group">
              <label>Confirmation Code</label>
              <Input
                type="text"
                value={confirmCodeValue}
                onChange={(e) => setConfirmCodeValue(e.target.value)}
                required
                placeholder="000000"
                maxLength={6}
                style={{
                  textAlign: 'center',
                  fontSize: '24px',
                  letterSpacing: '0.5em',
                }}
              />
            </div>
          )}

          <Button type="submit" loading={loading} fullWidth style={{ marginTop: '8px' }}>
            {mode === 'login' && 'Sign In'}
            {mode === 'register' && validatedInvite && 'Create Account & Join'}
            {mode === 'register' && canSignup && !validatedInvite && 'Create Account'}
            {mode === 'confirm' && 'Verify Email'}
            {mode === 'forgot' && 'Send Reset Code'}
            {mode === 'reset' && 'Reset Password'}
          </Button>

          {(mode === 'forgot' || mode === 'reset') && (
            <Button
              type="button"
              variant="secondary"
              iconLeft={<ArrowLeft size={16} />}
              onClick={() => {
                setMode('login');
                clearMessages();
                setResetSuccess('');
              }}
              fullWidth
              style={{ marginTop: '8px' }}
            >
              Back to Sign In
            </Button>
          )}
        </form>

        {/* OAuth providers - hide during password reset flow */}
        {(mode === 'login' || mode === 'register') &&
          (oauthProviders.google || oauthProviders.apple) && (
            <>
              <div className="auth-divider">
                <span>Or continue with</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {oauthProviders.google && (
                  <Button
                    type="button"
                    variant="secondary"
                    iconLeft={
                      <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                    }
                    onClick={() => signInWithGoogle()}
                    fullWidth
                  >
                    Continue with Google
                  </Button>
                )}
                {oauthProviders.apple && (
                  <Button
                    type="button"
                    variant="secondary"
                    iconLeft={
                      <svg
                        style={{ width: '20px', height: '20px' }}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                      </svg>
                    }
                    onClick={() => signInWithApple()}
                    fullWidth
                    style={{ background: 'white', color: 'black' }}
                  >
                    Continue with Apple
                  </Button>
                )}
              </div>
            </>
          )}

        {/* Mode toggle */}
        <div className="auth-toggle">
          {mode === 'login' && canSignup && (
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>
              Don't have an account?{' '}
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setMode('register');
                  clearMessages();
                }}
              >
                Sign up
              </Button>
            </p>
          )}
          {mode === 'register' && canSignup && (
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>
              Already have an account?{' '}
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setMode('login');
                  clearMessages();
                }}
              >
                Sign in
              </Button>
            </p>
          )}
          {mode === 'confirm' && (
            <Button
              type="button"
              variant="ghost"
              iconLeft={<ArrowLeft size={16} />}
              onClick={() => {
                setMode('login');
                clearMessages();
              }}
            >
              Back to login
            </Button>
          )}
        </div>

        {/* Athlete badge */}
        <div
          style={{
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'rgba(var(--purple-rgb), 0.1)',
              border: '1px solid rgba(var(--purple-rgb), 0.3)',
              borderRadius: '20px',
              color: 'var(--purple-400)',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Athlete Portal
          </span>
        </div>
      </div>
    </div>
  );
}
